import { useCallback, useEffect, useState } from "react";
import { Select, Table } from "antd";
import { Link } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import { api, type TopTalker } from "../api";

function fmtBps(v: number): string {
  if (v >= 1e9) return (v / 1e9).toFixed(2) + " G";
  if (v >= 1e6) return (v / 1e6).toFixed(2) + " M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(Math.round(v));
}
function fmtPps(v: number): string {
  if (v >= 1e6) return (v / 1e6).toFixed(2) + " M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(Math.round(v));
}

interface Props {
  metric: "bps" | "pps";
  dir: "in" | "out";
  color?: string;
  refreshMs?: number;
  geo?: "ixp"; // if set, shows IXP-only top talkers
}

const LIMIT_OPTIONS = [
  { label: "Top 10", value: 10 },
  { label: "Top 20", value: 20 },
  { label: "Top 50", value: 50 },
  { label: "Top 100", value: 100 },
];

export default function TopTalkersTable({
  metric,
  dir,
  color = "#1677ff",
  refreshMs = 5000,
  geo,
}: Props) {
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState<TopTalker[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.liveTop(metric, dir, limit, geo);
      setData(rows);
    } catch {
      /* keep stale */
    } finally {
      setLoading(false);
    }
  }, [metric, dir, limit, geo]);

  useEffect(() => {
    load();
    if (refreshMs > 0) {
      const id = window.setInterval(load, refreshMs);
      return () => window.clearInterval(id);
    }
  }, [load, refreshMs]);

  const fmt = metric === "bps" ? fmtBps : fmtPps;
  const unitLabel = metric === "bps" ? "Bits/s" : "Pkts/s";

  const columns: ColumnsType<TopTalker> = [
    {
      title: "No",
      dataIndex: "rank",
      width: 28,
      align: "right",
      render: (v: number) => <span style={{ color: "#778899" }}>{v}</span>,
    },
    {
      title: "IP Address",
      key: "ip",
      render: (_: unknown, row: TopTalker) => {
        const label = row.prefix || `node-${row.node_id}`;
        return row.prefix ? (
          <Link
            to={`/ip?q=${encodeURIComponent(row.prefix)}${geo ? `&view=${geo}` : ""}`}
            style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 600, color: "#1a4a7a" }}
          >
            {label}
          </Link>
        ) : (
          <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 600, color: "#1a4a7a" }}>
            {label}
          </span>
        );
      },
    },
    {
      title: unitLabel,
      key: "value",
      width: 80,
      align: "right",
      render: (_: unknown, row: TopTalker) => (
        <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 600 }}>
          {fmt(metric === "bps" ? row.bps : row.pps)}
        </span>
      ),
    },
    {
      title: (
        <Select
          size="small"
          value={limit}
          onChange={(v) => setLimit(v)}
          options={LIMIT_OPTIONS}
          style={{ width: 80, fontSize: 11 }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      dataIndex: "pct",
      width: 100,
      align: "right",
      render: (v: number) => (
        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <div style={{ flex: 1, height: 5, background: "#e0e4ea", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${Math.min(v, 100)}%`, height: "100%", background: color, borderRadius: 2 }} />
          </div>
          <span style={{ fontSize: 10, color: "#445", minWidth: 28, textAlign: "right" }}>
            {v.toFixed(0)}%
          </span>
        </div>
      ),
    },
  ];

  return (
    <Table
      rowKey={(r) => `${r.rank}-${r.prefix}`}
      size="small"
      loading={loading}
      dataSource={data}
      columns={columns}
      pagination={false}
      scroll={{ y: "calc(100% - 36px)" }}
      locale={{ emptyText: "No live data" }}
      style={{ height: "100%" }}
    />
  );
}
